import { type ApiError } from './types';

// ============================================================================
// Configuration
// ============================================================================

type Environment = 'local' | 'dev' | 'prod';

interface ApiConfig {
  baseUrl: string;
  timeout: number;
}

const configs: Record<Environment, ApiConfig> = {
  local: {
    // Admin-specific endpoints at /admin/v1
    baseUrl: 'http://localhost:15080/admin/v1',
    timeout: 10000,
  },
  dev: {
    baseUrl: 'https://dev.api.haulkeep.com/admin/v1',
    timeout: 15000,
  },
  prod: {
    baseUrl: 'https://api.haulkeep.com/admin/v1',
    timeout: 15000,
  },
};

function getEnvironment(): Environment {
  // Check for explicit environment override
  const envOverride = import.meta.env.VITE_API_ENV as Environment | undefined;
  if (envOverride && envOverride in configs) {
    return envOverride;
  }

  // Default based on Vite mode
  if (import.meta.env.DEV) {
    return 'local';
  }

  return 'prod';
}

const currentEnv = getEnvironment();
const config = configs[currentEnv];

// ============================================================================
// Token Management
// ============================================================================

let authToken: string | null = null;

/**
 * Set the authentication token for API requests.
 * Called after successful authentication.
 */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * Get the current authentication token.
 */
export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Clear the authentication token.
 * Called on logout.
 */
export function clearAuthToken(): void {
  authToken = null;
}

// ============================================================================
// API Client
// ============================================================================

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
  requireAuth?: boolean;
}

class ApiClientError extends Error {
  code: string;
  status?: number;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    status?: number,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Make an authenticated API request.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    timeout = config.timeout,
    requireAuth = true,
  } = options;

  // Build URL
  const url = `${config.baseUrl}${endpoint}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add auth header if token exists
  if (authToken && requireAuth) {
    requestHeaders['Authorization'] = `Bearer ${authToken}`;
  } else if (requireAuth && !authToken) {
    throw new ApiClientError(
      'Authentication required',
      'AUTH_REQUIRED',
      401
    );
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Parse response
    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      const error = data as ApiError;
      throw new ApiClientError(
        error.error?.message || 'Request failed',
        error.error?.code || 'UNKNOWN_ERROR',
        response.status,
        error.error?.details
      );
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort (timeout)
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiClientError('Request timeout', 'TIMEOUT', 408);
    }

    // Handle network errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new ApiClientError(
        'Network error - please check your connection',
        'NETWORK_ERROR'
      );
    }

    // Re-throw ApiClientError
    if (error instanceof ApiClientError) {
      throw error;
    }

    // Handle unknown errors
    throw new ApiClientError(
      'An unexpected error occurred',
      'UNKNOWN_ERROR'
    );
  }
}

// ============================================================================
// Convenience Methods
// ============================================================================

export const api = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

// ============================================================================
// Debug Utilities
// ============================================================================

/**
 * Get current API configuration (for debugging).
 */
export function getApiConfig(): { environment: Environment; baseUrl: string } {
  return {
    environment: currentEnv,
    baseUrl: config.baseUrl,
  };
}
